<template>
  <div>
    <div v-if="layout=='grid'">
      <v-container fluid class="ma-4 ml-6">
        <v-row>
          <v-card v-for="(item, index) in table.items" :key="index" class="ma-2">
            <v-card-title class="subtitle-1 pa-2 blue-grey white--text">{{item.displayName}}</v-card-title>
            <v-card-text class="pa-4">
              <table class="group-vcard">
                <tr>
                  <td>
                    <h4>Group ID:</h4>
                  </td>
                  <td>{{item.id}}</td>
                </tr>
                <tr>
                  <td>
                    <h4>Owner:</h4>
                  </td>
                  <td>{{item.owner}}</td>
                </tr>
              </table>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    small
                    color="blue"
                    class="ma-auto"
                    @click="editorOpen(item.id, 'summary')"
                  >
                    <v-icon>mdi-square-edit-outline</v-icon>
                  </v-btn>
                </template>
                <span>Edit group summary</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    small
                    color="blue"
                    class="ma-auto"
                    @click="editorOpen(item.id, 'members')"
                  >
                    <v-icon>mdi-account-group-outline</v-icon>
                  </v-btn>
                </template>
                <span>Edit group member list</span>
              </v-tooltip>
            </v-card-actions>
          </v-card>
        </v-row>
      </v-container>
    </div>
    <div v-if="layout=='list'" class="full-height">
      <v-card class="ma-2">
        <v-card-text>
          <v-container fluid class="pa-0 ma-0">
            <v-row class="pa-0 mx-2 my-0">
              <v-col class="pa-0 ma-0">
                <v-text-field
                  v-model="table.search"
                  append-icon="mdi-filter"
                  label="Filter table entries"
                  single-line
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="auto" class="pa-0 ma-0">
                <v-btn icon @click="load()" large class="my-2 mx-0">
                  <v-icon class="ma-0">mdi-reload</v-icon>
                </v-btn>
              </v-col>
              <v-col cols="auto" class="pa-0 ma-0">
                <v-menu v-model="settingsMenu" :close-on-content-click="false" offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn icon v-on="on" large class="my-2">
                      <v-icon>mdi-table-settings</v-icon>
                    </v-btn>
                  </template>
                  <v-card min-width="320px">
                    <v-list>
                      <v-list-item>
                        <v-list-item-title>Editor dialog box:</v-list-item-title>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-action>
                          <v-switch v-model="editor.persistent"></v-switch>
                        </v-list-item-action>
                        <v-list-item-title>Persistent mode</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-container fluid class>
          <v-data-table
            :headers="table.headers"
            :items="table.items"
            class="elevation-1"
            multi-sort
            filterable
            :options.sync="table.options"
            :search="table.search"
            :loading="table.loading"
          >
            <template v-slot:item.action="{ item }">
              <span class="actions">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      icon
                      small
                      class="ma-auto"
                      @click="editorOpen(item.id, 'summary')"
                    >
                      <v-icon>mdi-square-edit-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit group summary</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      icon
                      small
                      class="ma-auto"
                      @click="editorOpen(item.id, 'members')"
                    >
                      <v-icon>mdi-account-group-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit group member list</span>
                </v-tooltip>
              </span>
            </template>
          </v-data-table>
        </v-container>
      </v-card>
    </div>
    <v-dialog v-model="editor.active" width="unset" :persistent="editor.persistent">
      <Group
        :id="editor.id"
        :tab="editor.tab"
        @close="editorClose"
        @error="processErrorResponse"
        @success="displaySuccess"
      ></Group>
    </v-dialog>
  </div>
</template>

<style scoped>
table.group-vcard > tr > td:first-child > h4 {
  white-space: nowrap;
  padding-right: 5px;
  text-align: right;
}
span.actions {
  white-space: nowrap;
}
</style>

<script>
import Group from "../components/Group.vue";

import { getLocalParam, setLocalParam, makeMovableDialogs } from "./../lib";

makeMovableDialogs();

export default {
  name: "GroupList",
  components: { Group },
  computed: {
    userIsAdmin() {
      return this.$store.getters.userIsAdmin;
    }
  },
  props: {
    layout: {
      type: String,
      default: "list",
      validator: value => ["list", "grid"].indexOf(value) !== -1
    },
    id: {
      default: "",
      type: String
    }
  },
  data: () => ({
    editor: {
      id: null,
      tab: null,
      active: false,
      persistent: getLocalParam("settings.groupList.editor.persistent", true)
    },
    settingsMenu: false,
    table: {
      items: [],
      options: null,
      loading: false,
      search: "",
      index: -1,
      headers: [
        { text: "Name", align: "left", sortable: true, value: "id" },
        { text: "Actions", value: "action", align: "right", sortable: false },
        {
          text: "Display Name",
          align: "left",
          sortable: true,
          value: "displayName"
        },
        { text: "Owner", align: "left", sortable: true, value: "owner" }
      ]
    }
  }),
  watch: {
    id(value) {
      if (value) this.editorOpen(value);
    },
    layout(value) {
      setLocalParam("settings.configList.layout", value);
    },
    listType(value) {
      setLocalParam("settings.configList.listType", value);
      this.load();
    },
    "editor.persistent": function(value) {
      setLocalParam("settings.groupList.editor.persistent", value);
    },
    "table.options": {
      deep: true,
      handler(options) {
        setLocalParam("settings.groupList.table", options);
      }
    }
  },
  created() {
    this.table.options = getLocalParam("settings.groupList.table", {
      multiSort: true,
      mustSort: false
    });
    this.load();
    if (this.id) {
      this.editorOpen(this.id);
    }
  },
  methods: {
    processErrorResponse(result) {
      this.$emit("error", result);
    },
    displaySuccess(result) {
      this.$emit("success", result);
    },
    load() {
      this.tableLoading = true;
      this.$axios
        .get(
          `/groups?${
            this.userIsAdmin ? "global=true&" : ""
          }extended_format=true&limit=65536`
        )
        .then(result => {
          this.table.items = result.data.map(elt => {
            return {
              id: elt.group_id,
              displayName: elt.display_name || elt.group_id,
              owner: elt.owner_id
            };
          });
          this.tableLoading = false;
        })
        .catch(result => {
          this.processErrorResponse(result);
          this.tableLoading = false;
        });
    },
    editorOpen(id, tab) {
      if (this.editor.active) return;
      this.editor.active = true;
      this.editor.id = id;
      this.editor.tab = tab;
      this.table.index = this.table.items.findIndex(
        e => e.id === this.editor.id
      );
      if (this.table.index === -1) {
        this.table.index = this.table.items.length;
        this.table.items.push({
          id: this.editor.id
        });
      }
    },
    editorClose(result) {
      this.editor.active = false;
      if (!result || this.table.index < 0) return;
      switch (result.type) {
        case "update":
          this.table.items.splice(this.table.index, 1);
          this.table.items.splice(this.table.index, 0, result.data);
          break;
        case "delete":
          this.table.items.splice(this.table.index, 1);
          break;
      }
      this.table.index = -1;
      if (this.id) {
        this.$router.push(`/app/groups/${this.layout}`);
      }
    }
  }
};
</script>
